import React from 'react';
import { ApolloProvider } from '@apollo/client';
import './App.css';
import { apolloClient } from './config/apollo-client';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Auth from './components/Auth';
import Home from './components/Home';
import HomePage from './components/Home/HomePage';
import DistributorPage from './components/Distributor/DistributorPage';
import DistributorCreatePage from './components/Distributor/DistributorCreatePage';
import ShopPage from './components/Shop/ShopPage';
import ShopCreatePage from './components/Shop/ShopCreatePage';
import PlayerPage from './components/Player/PlayerPage';
import PlayerCreatePage from './components/Player/PlayerCreatePage';
import PlayerSuccessPage from './components/Player/PlayerSuccessPage';
import DistributorSuccessPage from './components/Distributor/DistributorSuccessPage';
import ShopSuccessPage from './components/Shop/ShopSuccessPage';
import UserPage from './components/User/UserPage';
import SubDistributorPage from './components/SubDistributor/SubDistributorPage';
import AgentPage from './components/Agent/AgentPage';
import GameManagement from './components/GameManagement';
import EnterGame from './components/EnterGame';
import SessionExpirationHandler from './components/SessionExpirationHandler';
import { authService } from './services/auth.service';
import SubDistributorCreatePage from './components/SubDistributor/SubDistributorCreatePage';
import AgentCreatePage from './components/Agent/AgentCreatePage';
import AgentSuccessPage from './components/Agent/AgentSuccessPage';
import SubDistributorSuccessPage from './components/SubDistributor/SubDistributorSuccessPage';
import PermissionsPage from './components/Permissions';
import TransactionsPage from './components/Transactions/TransactionsPage';
import { GameHistoryPage } from './components/GameHistory';
import ProviderBalance from './components/ProviderBalance/ProviderBalance';
import Configurations from './components/Configurations/Configurations';
import Notifications from './components/Notifications';
import ATMController from './components/ATMController';
import ATMCreate from './components/ATMController/ATMCreate';
import ATMEdit from './components/ATMController/ATMEdit';
import ATMHistory from './components/ATMHistory';
import Promo from './components/Promo';
import PromoCreate from './components/Promo/PromoCreate';
import Raffle from './components/Raffle';
import RaffleCreate from './components/Raffle/RaffleCreate';
import RaffleDetail from './components/Raffle/RaffleDetail';
import Support from './components/Support';
import OnlineTransactionsPage from './components/OnlineTransactions';
import WithdrawReview from './components/WithdrawReview';

const isAuthenticated = () => {
  // Use auth service to check authentication status
  return authService.isAuthenticated();
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ATM managers only ever see the ATM Controller.
  if (
    authService.getStoredUser()?.role.name === 'ATM_MANAGER' &&
    !location.pathname.startsWith('/atm-controller')
  ) {
    return <Navigate to="/atm-controller" replace />;
  }

  return <>{children}</>;
};

const RequireAdminAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const user = authService.getStoredUser();
  if (!user || user.role.name !== 'ADMIN') {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

const JackPotPage: React.FC = () => (
  <main style={{ padding: 32, fontSize: 24 }}>
    <p>JackPot Page</p>
  </main>
);

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <Router>
        <SessionExpirationHandler>
          <Routes>
            <Route path="/login" element={<Auth />} />
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <RequireAuth>
                  <Home>
                    <HomePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/distributor"
              element={
                <RequireAuth>
                  <Home>
                    <DistributorPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/distributor/create"
              element={
                <RequireAuth>
                  <Home>
                    <DistributorCreatePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/distributor/success"
              element={
                <RequireAuth>
                  <Home>
                    <DistributorSuccessPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/user/:id"
              element={
                <RequireAuth>
                  <Home>
                    <UserPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/shop"
              element={
                <RequireAuth>
                  <Home>
                    <ShopPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/shop/create"
              element={
                <RequireAuth>
                  <Home>
                    <ShopCreatePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/shop/success"
              element={
                <RequireAuth>
                  <Home>
                    <ShopSuccessPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/player"
              element={
                <RequireAuth>
                  <Home>
                    <PlayerPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/player/create"
              element={
                <RequireAuth>
                  <Home>
                    <PlayerCreatePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/player/success"
              element={
                <RequireAuth>
                  <Home>
                    <PlayerSuccessPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/permissions"
              element={
                <RequireAdminAuth>
                  <Home>
                    <PermissionsPage />
                  </Home>
                </RequireAdminAuth>
              }
            />
            <Route
              path="/withdraw-review"
              element={
                <RequireAdminAuth>
                  <Home>
                    <WithdrawReview />
                  </Home>
                </RequireAdminAuth>
              }
            />
            <Route
              path="/sub-distributor"
              element={
                <RequireAuth>
                  <Home>
                    <SubDistributorPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/subdistributor/create"
              element={
                <RequireAuth>
                  <Home>
                    <SubDistributorCreatePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/sub-distributor/success"
              element={
                <RequireAuth>
                  <Home>
                    <SubDistributorSuccessPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/agent"
              element={
                <RequireAuth>
                  <Home>
                    <AgentPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/agent/create"
              element={
                <RequireAuth>
                  <Home>
                    <AgentCreatePage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/agent/success"
              element={
                <RequireAuth>
                  <Home>
                    <AgentSuccessPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/transactions"
              element={
                <RequireAuth>
                  <Home>
                    <TransactionsPage />
                  </Home>
                </RequireAuth>
              }
            />
            {authService.getStoredUser()?.role.name !== 'SHOP' && (

              <Route
                path="/game-history"
                element={
                  <RequireAuth>
                    <Home>
                      <GameHistoryPage />
                    </Home>
                  </RequireAuth>
                }
              />
            )}

            <Route
              path="/game-management"
              element={
                <RequireAuth>
                  <Home>
                    <GameManagement />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/atm-controller"
              element={
                <RequireAuth>
                  <Home>
                    <ATMController />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/atm-history"
              element={
                <RequireAdminAuth>
                  <Home>
                    <ATMHistory />
                  </Home>
                </RequireAdminAuth>
              }
            />
            <Route
              path="/atm-controller/create"
              element={
                <RequireAuth>
                  <Home>
                    <ATMCreate />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/atm-controller/:id"
              element={
                <RequireAuth>
                  <Home>
                    <ATMEdit />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/provider-balance"
              element={
                <RequireAuth>
                  <Home>
                    <ProviderBalance />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/configurations"
              element={
                <RequireAuth>
                  <Home>
                    <Configurations />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/notifications"
              element={
                <RequireAuth>
                  <Home>
                    <Notifications />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/promo"
              element={
                <RequireAuth>
                  <Home>
                    <Promo />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/raffle"
              element={
                <RequireAuth>
                  <Home>
                    <Raffle />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/raffle/create"
              element={
                <RequireAuth>
                  <Home>
                    <RaffleCreate />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/raffle/:id"
              element={
                <RequireAuth>
                  <Home>
                    <RaffleDetail />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/promo/create"
              element={
                <RequireAuth>
                  <Home>
                    <PromoCreate />
                  </Home>
                </RequireAuth>
              }
            />
            {/* {authService.getStoredUser()?.role.name !== 'SHOP' && (
              <Route
                path="/jackpot"
                element={
                  <RequireAuth>
                    <Home>
                    <JackPotPage />
                  </Home>
                </RequireAuth>
              }
            />
            )} */}
            <Route
              path="/online-transactions"
              element={
                <RequireAuth>
                  <Home>
                    <OnlineTransactionsPage />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/support"
              element={
                <RequireAuth>
                  <Home>
                    <Support />
                  </Home>
                </RequireAuth>
              }
            />
            <Route
              path="/entergame"
              element={
                <RequireAuth>
                  <Home>
                    <EnterGame />
                  </Home>
                </RequireAuth>
              }
            />
          </Routes>
        </SessionExpirationHandler>
      </Router>
    </ApolloProvider>
  );
}

export default App;
